// Export-only, unexposed history format. Undo documents are never presented as
// pending saves. The storage wrapper requires the caller's storage→Site→images
// read leases and a live synchronous account/session guard.
import { HISTORY_LIMITS, historyError, historyJSON, historyImageIDs, canonicalHistoryDocument, validateHistoryCheckpoint, validateHistoryState, validateHistoryAssetEvidence } from './editorHistory.js';
import { listRetainedEditorHistory, readRetainedEditorHistory } from './editorHistoryRecovery.js';

const encoder = new TextEncoder();
const maxArchiveBytes = 64 * 1024 * 1024;
const maxZipOverhead = 1024 * 1024;
const extensions = new Map([['image/png','png'],['image/jpeg','jpg'],['image/webp','webp'],['image/gif','gif'],['image/svg+xml','svg']]);
const digest = async bytes => [...new Uint8Array(await crypto.subtle.digest('SHA-256',bytes))].map(v => v.toString(16).padStart(2,'0')).join('');
const readme = `Avisera Planner — retained editor history archive v1

history.json contains separate present, Undo and Redo document snapshots.
They are not queued saves or server versions. manifest.json identifies the
original image files and their verified retained SHA-256 checksums.

This archive is for safekeeping and manual inspection. Avisera does not yet
import it or attach its Undo stack automatically. Source generation and revision
are context, not permission to replace a current Site. Account credentials,
transport headers, upload retry descriptors and live attachment tokens are not
added to this archive; those remain in retained device storage. Plan documents
are preserved as recorded and may contain sensitive Site information.

Only images referenced by these history documents are included. Unreferenced
retained originals, queued saves, device backups, private field-photo collections
and native scan archives are separate. A PDF drawing is its rendered image page,
not its source PDF. Embedded evidence does not certify measurement accuracy.
SVG images may contain active content; treat files as untrusted outside Avisera.

Export does not save, upload, replay, restore, detach or remove history. Keep the
device copy until you have verified this archive at its destination.
`;

function guard({assertCurrent,signal}) {
  if(typeof assertCurrent !== 'function')throw historyError('An active-account guard is required.','history-account');
  return async () => {
    if(signal?.aborted)throw new DOMException('History export cancelled.','AbortError');
    if(await assertCurrent()===false)throw historyError('The owning history session is no longer current.','history-account');
    if(signal?.aborted)throw new DOMException('History export cancelled.','AbortError');
  };
}

export async function buildEditorHistoryArchive(selected,{assertCurrent,signal,onProgress,generatedAt=new Date().toISOString()}={}) {
  const check=guard({assertCurrent,signal});
  // Detach metadata before any await; the byte Blobs themselves are immutable.
  const checkpoint=JSON.parse(historyJSON(selected?.checkpoint));
  validateHistoryCheckpoint(checkpoint);
  const history=validateHistoryState(selected?.history);
  if(historyJSON(history)!==historyJSON({present:checkpoint.current,undo:checkpoint.undo,redo:checkpoint.redo}))throw historyError('History documents do not match the selected checkpoint.');
  const state=selected?.state;
  if(!['active','stale','orphan'].includes(state))throw historyError('Choose readable retained history before exporting.');
  if(typeof generatedAt!=='string'||!Number.isFinite(Date.parse(generatedAt)))throw historyError('The history archive timestamp is invalid.');
  const imageIDs=historyImageIDs([history.present,...history.undo,...history.redo]);
  if(!Array.isArray(selected.assets)||selected.assets.length>HISTORY_LIMITS.assets||selected.assets.some(a=>!a||typeof a!=='object')||new Set(selected.assets.map(a=>a.imageID)).size!==selected.assets.length)throw historyError('The retained image list is ambiguous.');
  const assets=new Map(selected.assets.map(a=>[a.imageID,{...JSON.parse(historyJSON(Object.fromEntries(Object.entries(a).filter(([key])=>key!=='blob')))),blob:a.blob}]));
  const payload={format:'avisera-editor-history',version:1,source:{projectID:checkpoint.projectID,savedAt:checkpoint.savedAt,state,generation:checkpoint.anchor.generation,revision:checkpoint.anchor.rev,documentSHA256:checkpoint.anchor.documentSHA256},history};
  const historyBytes=encoder.encode(historyJSON(payload));
  let total=historyBytes.length;
  for(const imageID of imageIDs){
    const asset=assets.get(imageID);
    if(!asset||asset.account!==checkpoint.account||asset.projectID!==checkpoint.projectID||!(asset.blob instanceof Blob)||!extensions.has(asset.blob.type)||asset.blob.type!==asset.mime||asset.blob.size!==asset.size||asset.size<=0||asset.size>HISTORY_LIMITS.imageBytes||!/^[a-f0-9]{64}$/.test(asset.sha256))throw historyError('Every history drawing needs its verified retained original.','history-assets-unavailable');
    validateHistoryAssetEvidence(asset);total+=asset.size;
  }
  if(total>maxArchiveBytes)throw historyError('The history and drawings exceed the 64 MiB archive limit. No partial archive was created.','history-limit');
  await check();
  if(await digest(encoder.encode(canonicalHistoryDocument(history.present)))!==checkpoint.anchor.documentSHA256)throw historyError('The retained document failed its anchor checksum.');
  await check();
  const {Zip,ZipPassThrough}=await import('three/examples/jsm/libs/fflate.module.js');
  await check();
  const parts=[];let bytes=0,failure,finished=false;
  const zip=new Zip((error,data,final)=>{
    if(error){failure=error;return;}
    bytes+=data.length;
    if(bytes>maxArchiveBytes+maxZipOverhead){failure=historyError('The ZIP exceeds its bounded archive limit.','history-limit');return;}
    parts.push(new Blob([data]));if(final)finished=true;
  });
  const append=(name,data)=>{
    const file=new ZipPassThrough(name);file.mtime=new Date('2020-01-01T00:00:00Z');zip.add(file);file.push(data,true);
    if(failure)throw failure;
  };
  const images=[];
  try{
    append('history.json',historyBytes);
    for(const [index,imageID]of imageIDs.entries()){
      await check();const asset=assets.get(imageID),data=new Uint8Array(await asset.blob.arrayBuffer());await check();
      if(await digest(data)!==asset.sha256)throw historyError('A retained history original failed its checksum. No partial archive was created.','history-assets-unavailable');
      await check();
      if(asset.descriptor){
        const d=asset.descriptor,identity='ref_'+(await digest(encoder.encode(`${checkpoint.account}/${d.projectID}/${d.uploadID}`))).slice(0,32);
        await check();if(identity!==imageID)throw historyError('The retained upload identity does not match its drawing.');
      }
      const path='images/'+String(index+1).padStart(4,'0')+'.'+extensions.get(asset.mime);
      append(path,data);
      images.push({imageID,path,mime:asset.mime,bytes:data.length,sha256:asset.sha256,verification:'retained-original-checksum',...(asset.descriptor?{retainedUploadState:asset.descriptor.state,...(asset.descriptor.blockedStatus?{retainedBlockedStatus:asset.descriptor.blockedStatus}:{})}:{})});
      onProgress?.({phase:'packing',completed:index+1,total:imageIDs.length,bytes});
      await new Promise(resolve=>setTimeout(resolve,0));
    }
    const manifest={format:'avisera-editor-history-archive',version:1,generatedAt,history:'history.json',images,complete:true};
    append('manifest.json',encoder.encode(JSON.stringify(manifest,null,2)));append('README.txt',encoder.encode(readme));zip.end();
    if(failure||!finished)throw failure||historyError('The history archive did not finish.');
    await check();
    const blob=new Blob(parts,{type:'application/zip'});
    return {blob,filename:'avisera-history-'+generatedAt.slice(0,10).replace(/[^0-9-]/g,'')+'.zip',bytes:blob.size,assetCount:images.length,savedAt:checkpoint.savedAt,manifest};
  }finally{zip.terminate();parts.length=0;}
}

export async function exportRetainedEditorHistory(database,account,selection,options={}) {
  const chosen={projectID:selection?.projectID,token:selection?.token};
  const selected=await readRetainedEditorHistory(database,account,chosen,options);
  const result=await buildEditorHistoryArchive(selected,options);
  const current=await listRetainedEditorHistory(database,account,options);
  const entry=current.entries.find(value=>value.projectID===chosen.projectID);
  if(!current.complete||!entry?.readable||entry.token!==chosen.token)throw historyError('The selected history changed during export. Choose it again.','history-stale');
  return result;
}
